import * as React from "react";
import { Tearsheet } from "@procore/core-react";
import { AssignmentTearsheetContent } from "../assignment/assignment-tearsheet-content";
import { SendOneOffAssignmentAlertsTearsheetContent } from "./send-one-off-assignment-alerts-tearsheet-content";
import type { ResourceType, FormSelectOption } from "@/react/prop-types";
import type { Equipment } from "@procore/equipment-select";
import type { TableApi } from "@procore/data-table";
import type { CannedMessageType } from "@laborchart-modules/common/dist/postgres/schemas/common/enums";
import { SendAssignmentAlertsTearsheetContent } from "./send-assignment-alerts-tearsheet-content";
import { EditAssignmentAlertTearsheetContent } from "./edit-assignment-alert-tearsheet-content";
import type { TransferAssignment } from "./transfer-assignments-tearsheet-content";
import { TransferAssignmentsTearsheetContent } from "./transfer-assignments-tearsheet-content";
import type { EditAlertData } from "@/react/components/communications/communications-prop-types";

type AssignmentTearsheetState = OpenState | ClosedState;

// Common properties shared across all open states
type BaseOpenState = {
   isOpen: true;
   projectId?: string;
   callback?: (data: any) => void;
   onClose?: () => void;
};

type OpenState = BaseOpenState &
   (
      | CreateAssignment
      | EditAssignment
      | SendOneOffAssignmentAlerts
      | SendAssignmentAlerts
      | EditAssignmentAlert
      | TransferAssignments
   );
type ClosedState = { isOpen: false; show: null };

type CreateAssignment = {
   show: "create-assignment";
   projectId?: string;
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
   personId?: string;
   personName?: string;
   people?: Array<FormSelectOption<string>>; // For multi-person assignments
   equipment?: Equipment[]; // For pre-filling equipment in drag-to-create
   // Pre-selects resource type when launched from a typed entry point
   // (e.g. equipment drag). Undefined → labor default.
   resourceType?: ResourceType;
   startDate?: Date;
   endDate?: Date;
};
type EditAssignment = {
   show: "edit-assignment";
   assignmentId: string;
   people?: Array<FormSelectOption<string>>; // For multi-person assignments
   resourceType: ResourceType;
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
   callback?: (data: any) => void;
   onClose?: () => void;
};
type SendOneOffAssignmentAlerts = {
   show: "send-one-off-assignment-alerts";
   startDate?: Date | null;
   endDate?: Date | null;
   viewingDay?: Date;
   resources?: Array<FormSelectOption<string>>;
   showProjectDetail?: (projectId: string) => void;
};
type SendAssignmentAlerts = {
   show: "send-assignment-alerts";
   assignments: Array<{
      id: string;
      recipientIds: string[];
   }>;
   alertType: CannedMessageType;
};
type EditAssignmentAlert = {
   show: "edit-assignment-alert";
   editAlertData: EditAlertData;
   callback?: () => void;
};

type TransferAssignments = {
   show: "transfer-assignments";
   projectId: string;
   callback: (data: any) => void;
   endDay: number;
   assignments: TransferAssignment[];
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
};
type CloseAction = { type: "close" };
export type CreateAssignmentAction = {
   type: "open-create-assignment";
   projectId?: string;
   people?: Array<FormSelectOption<string>>; // For multi-person assignments
   equipment?: Equipment[]; // For pre-filling equipment in drag-to-create
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
   callback?: (data: any) => void;
   onClose?: () => void;
   resourceType?: ResourceType;
   startDate?: Date;
   endDate?: Date;
};
type EditAssignmentAction = {
   type: "open-edit-assignment";
   assignmentId: string;
   people?: Array<FormSelectOption<string>>; // For multi-person assignments
   projectId?: string;
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
   callback?: (data: any) => void;
   onClose?: () => void;
   resourceType: ResourceType;
};
type SendOneOffAssignmentAlertsAction = {
   type: "open-send-one-off-assignment-alerts";
   projectId: string;
   callback?: (data: any) => void;
   startDate?: Date | null;
   endDate?: Date | null;
   viewingDay?: Date;
   resources?: Array<FormSelectOption<string>>;
   showProjectDetail?: (projectId: string) => void;
};

type SendAssignmentAlertsAction = {
   projectId: string;
   type: "open-send-assignment-alerts";
   assignments: Array<{
      id: string;
      recipientIds: string[];
   }>;
   alertType: CannedMessageType;
   onClose?: () => void;
};

type TransferAssignmentsAction = {
   type: "open-transfer-assignments";
   projectId: string;
   endDay: number;
   callback: (data: any) => void;
   assignments: TransferAssignment[];
   // categoryId/subcategoryId: undefined = use DB data, null = explicitly no category, string = use this category
   categoryId?: string | null;
   subcategoryId?: string | null;
   onClose?: () => void;
};
type EditAssignmentAlertAction = {
   type: "open-edit-assignment-alert";
   editAlertData: EditAlertData;
   callback?: () => void;
};
export type AssignmentTearsheetActions =
   | CloseAction
   | CreateAssignmentAction
   | EditAssignmentAction
   | SendOneOffAssignmentAlertsAction
   | SendAssignmentAlertsAction
   | EditAssignmentAlertAction
   | TransferAssignmentsAction;
export const reducer = (
   _state: AssignmentTearsheetState,
   action: AssignmentTearsheetActions,
): AssignmentTearsheetState => {
   switch (action.type) {
      case "open-create-assignment": {
         return {
            isOpen: true,
            show: "create-assignment",
            projectId: action.projectId,
            categoryId: action.categoryId,
            subcategoryId: action.subcategoryId,
            people: action.people,
            equipment: action.equipment,
            callback: action.callback,
            onClose: action.onClose,
            resourceType: action.resourceType,
            startDate: action.startDate,
            endDate: action.endDate,
         };
      }
      case "open-edit-assignment": {
         return {
            isOpen: true,
            show: "edit-assignment",
            assignmentId: action.assignmentId,
            projectId: action.projectId,
            categoryId: action.categoryId,
            subcategoryId: action.subcategoryId,
            callback: action.callback,
            onClose: action.onClose,
            resourceType: action.resourceType,
            people: action.people,
         };
      }
      case "open-send-one-off-assignment-alerts": {
         return {
            isOpen: true,
            show: "send-one-off-assignment-alerts",
            projectId: action.projectId,
            callback: action.callback,
            startDate: action.startDate,
            endDate: action.endDate,
            viewingDay: action.viewingDay,
            resources: action.resources,
            showProjectDetail: action.showProjectDetail,
            onClose: _state.isOpen && "onClose" in _state ? _state.onClose : undefined, // Preserve parent tearsheet's onClose
         };
      }
      case "open-send-assignment-alerts": {
         return {
            isOpen: true,
            show: "send-assignment-alerts",
            projectId: action.projectId,
            assignments: action.assignments,
            alertType: action.alertType,
         };
      }
      case "open-edit-assignment-alert": {
         return {
            isOpen: true,
            show: "edit-assignment-alert",
            editAlertData: action.editAlertData,
            callback: action.callback,
         };
      }
      case "open-transfer-assignments": {
         return {
            isOpen: true,
            show: "transfer-assignments",
            projectId: action.projectId,
            assignments: action.assignments,
            endDay: action.endDay,
            callback: action.callback,
            categoryId: action.categoryId,
            subcategoryId: action.subcategoryId,
            onClose: action.onClose,
         };
      }
      case "close": {
         return {
            isOpen: false,
            show: null,
         };
      }
   }
};

export const initialState: AssignmentTearsheetState = {
   isOpen: false,
   show: null,
};

const AssignmentTearsheetContext = React.createContext<
   | (AssignmentTearsheetState & {
        dispatch: React.Dispatch<AssignmentTearsheetActions>;
        onClose?: () => void;
     })
   | null
>(null);

export const AssignmentTearsheetProvider: React.FC<{
   children: React.ReactNode;
   assignmentTableApi: TableApi | undefined;
   onClose?: () => void;
}> = ({ children, assignmentTableApi, onClose }) => {
   const [state, dispatch] = React.useReducer(reducer, initialState);

   const contextValue = React.useMemo(
      () => ({ ...state, dispatch, onClose: ("onClose" in state && state.onClose) || onClose }),
      [state, dispatch, onClose],
   );

   return (
      <AssignmentTearsheetContext.Provider value={contextValue}>
         {children}
         <AssignmentTearsheet assignmentTableApi={assignmentTableApi} state={contextValue} />
      </AssignmentTearsheetContext.Provider>
   );
};

export const useAssignmentTearsheet = () => {
   const context = React.useContext(AssignmentTearsheetContext);

   if (!context) {
      throw Error(
         "'useAssignmentTearsheet' is supposed to be used in the component that is a child of 'AssignmentTearsheetProvider'.",
      );
   }

   return context;
};

export const AssignmentTearsheet: React.FC<{
   assignmentTableApi: TableApi | undefined;
   state: AssignmentTearsheetState & {
      dispatch: React.Dispatch<AssignmentTearsheetActions>;
      onClose?: () => void;
   };
}> = ({ assignmentTableApi, state }) => {
   const isEditAssignmentOpen = state.show === "edit-assignment";
   const isCreateAssignmentOpen = state.show === "create-assignment";
   const isSendOneOffAssignmentAlertsOpen = state.show === "send-one-off-assignment-alerts";
   const isSendAssignmentAlertsOpen = state.show === "send-assignment-alerts";
   const isEditAssignmentAlertOpen = state.show === "edit-assignment-alert";
   const isTransferAssignmentsOpen = state.show === "transfer-assignments";

   const handleClose = () => {
      state.onClose?.();
      state.dispatch({ type: "close" });
   };

   const onCreateEditAssignment = () => {
      assignmentTableApi?.refreshServerSide({});
   };

   return (
      <Tearsheet open={state.isOpen} onClose={handleClose}>
         {isCreateAssignmentOpen && (
            <AssignmentTearsheetContent
               onClose={handleClose}
               projectId={state.projectId}
               categoryId={state.categoryId}
               subcategoryId={state.subcategoryId}
               personId={state.personId}
               personName={state.personName}
               people={state.people}
               equipment={state.equipment}
               callback={state.callback}
               resourceType={state.resourceType}
               onCreateEditAssignment={onCreateEditAssignment}
               startDate={state.startDate}
               endDate={state.endDate}
            />
         )}
         {isEditAssignmentOpen && (
            <AssignmentTearsheetContent
               onClose={handleClose}
               assignmentId={state.assignmentId}
               people={state.people}
               projectId={state.projectId}
               categoryId={state.categoryId}
               subcategoryId={state.subcategoryId}
               callback={state.callback}
               resourceType={state.resourceType}
               onCreateEditAssignment={onCreateEditAssignment}
            />
         )}
         {isSendOneOffAssignmentAlertsOpen && state.projectId && (
            <SendOneOffAssignmentAlertsTearsheetContent
               onClose={handleClose}
               projectId={state.projectId}
               startDate={state.startDate}
               endDate={state.endDate}
               viewingDay={state.viewingDay}
               resources={state.resources}
               onShowProjectDetail={state.showProjectDetail}
               callback={state.callback}
            />
         )}
         {isSendAssignmentAlertsOpen && state.projectId && (
            <SendAssignmentAlertsTearsheetContent
               onClose={handleClose}
               projectId={state.projectId}
               assignments={state.assignments}
               alertType={state.alertType}
            />
         )}
         {isEditAssignmentAlertOpen && (
            <EditAssignmentAlertTearsheetContent
               editAlertData={state.editAlertData}
               onClose={handleClose}
               onSaved={() => state.callback?.()}
            />
         )}
         {isTransferAssignmentsOpen && state.projectId && (
            <TransferAssignmentsTearsheetContent
               onClose={handleClose}
               projectId={state.projectId}
               assignments={state.assignments}
               endDay={state.endDay}
               callback={state.callback}
               categoryId={state.categoryId}
               subcategoryId={state.subcategoryId}
            />
         )}
      </Tearsheet>
   );
};

export const onCreateAssignmentClick = (dispatch: React.Dispatch<AssignmentTearsheetActions>) => {
   dispatch({ type: "open-create-assignment" });
};

export const onEditAssignmentClick = (
   dispatch: React.Dispatch<AssignmentTearsheetActions>,
   assignmentId: string,
   resourceType: ResourceType,
) => {
   dispatch({ type: "open-edit-assignment", assignmentId, resourceType });
};
